import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, FormCheck } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useGetDishSupplementsListForUpdateMenuItemQuery } from "@me-team/host/main/updateMenuItem/graphql/updateMenuItem.hooks";
import { RenderGroupLabel } from "./RenderGroupLabel";
import { ClearValueBtn } from "@services/ui-components";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useMenuLanguagesData } from "../../../utils/hooks/useMenuLanguagesData";

export interface DishSupplementsModalProps {
    show: boolean;
    onClose: () => void;
    onCreateNew: () => void;
    initialSelectedIds: number[];
    onApply: (selectedIds: number[]) => void;
    onRefetchReady?: React.Dispatch<React.SetStateAction<(() => Promise<unknown>) | null>>;
    versionLocale?: string;
    canCreateNewSupplement?: boolean;
}

export interface SupplementOptions {
    id: number;
    name: string;
    nameOrigin?: string | null;
    price: number;
    deleted: boolean;
}

export interface MenuSupplementGroup {
    id: number;
    name: string;
    nameOrigin?: string | null;
    options: SupplementOptions[];
}

const AddSupplementsToDishModal: React.FC<DishSupplementsModalProps> = ({
    show,
    onClose,
    onCreateNew,
    initialSelectedIds,
    onApply,
    onRefetchReady,
    versionLocale,
    canCreateNewSupplement = false,
}) => {
    const { t } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const menuLanguageVersion = useReactiveVar(menuLanguageVersionVar);
    const selectedLangVersionCode = versionLocale ?? menuLanguageVersion;
    const { primaryLanguage } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageIso = primaryLanguage?.isoCode?.trim() || null;
    const isTranslationMode = Boolean(
        selectedLangVersionCode && primaryLanguageIso && selectedLangVersionCode !== primaryLanguageIso
    );
    const translationTooltip = t("Edit the language version");

    const [searchInput, setSearchInput] = useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");

    const [selectedIds, setSelectedIds] = useState<number[]>(initialSelectedIds);

    const handleClearSearch = () => {
        setSearchInput("");
        setDebouncedSearch("");
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearch(searchInput.trim());
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchInput]);

    useEffect(() => {
        if (show) {
            setSelectedIds(initialSelectedIds || []);
        }
    }, [show, initialSelectedIds]);

    const {
        data: dishSupplementsData,
        loading: dishSupplementsLoading,
        refetch: dishSupplementsRefetch,
    } = useGetDishSupplementsListForUpdateMenuItemQuery({
        variables: {
            companyId: Number(currentCompanyId),
            isDeleted: false,
            search: debouncedSearch || null,
            onlyParent: true,
            locale: selectedLangVersionCode,
        },
        skip: !currentCompanyId || !selectedLangVersionCode,
        context: {
            errorType: "local",
        },
        fetchPolicy: "network-only",
        onError: (error) => {
            console.error(error);
        },
    });

    const currencySign: string = dishSupplementsData?.user?.company?.currency?.currencySign;

    useEffect(() => {
        if (show && currentCompanyId) {
            dishSupplementsRefetch({
                companyId: Number(currentCompanyId),
                search: debouncedSearch || null,
                locale: selectedLangVersionCode,
            });
        }
    }, [show, debouncedSearch, currentCompanyId, dishSupplementsRefetch, selectedLangVersionCode]);

    const supplementGroups: MenuSupplementGroup[] = useMemo(() => {
        if (!dishSupplementsData) return [];

        const supplementsList = dishSupplementsData?.user?.company?.menuSupplementsList ?? [];
        return supplementsList.map((group) => ({
            id: group.id,
            name: group.groupName,
            nameOrigin: group.groupNameOrigin,
            options:
                group.options?.map((opt) => ({
                    id: opt.id,
                    name: opt.name,
                    nameOrigin: opt.nameOrigin,
                    price: opt.price ?? null,
                    deleted: opt.deleted,
                })) ?? [],
        }));
    }, [dishSupplementsData]);

    const handleToggleGroup = (groupId: number) => {
        setSelectedIds((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
    };

    const handleAddClick = () => {
        onApply(selectedIds);
        onClose();
    };

    const handleCreateNewClick = () => {
        if (!canCreateNewSupplement) return;
        onClose();
        onCreateNew();
    };

    const hasSelection = selectedIds.length > 0;

    useEffect(() => {
        if (!show) {
            setSearchInput("");
            setDebouncedSearch("");
        }
    }, [show]);

    useEffect(() => {
        if (onRefetchReady) {
            onRefetchReady(() => dishSupplementsRefetch);
        }
    }, [onRefetchReady, dishSupplementsRefetch]);

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton className="border-bottom-0">
                <Modal.Title className="fs-5">{t("Add additives to the dish")}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form className="px-6">
                    <Form.Group className="mb-3 position-relative" controlId="searchSupplement">
                        <Form.Control
                            type="text"
                            placeholder={t("Search")}
                            value={searchInput}
                            className="fs-normal pe-4"
                            onChange={(e) => setSearchInput(e.target.value)}
                            maxLength={50}
                        />
                        {searchInput.trim().length > 0 && <ClearValueBtn onClearValue={handleClearSearch} right={12} />}
                    </Form.Group>

                    <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                        {dishSupplementsLoading && <div className="text-muted fs-7 mb-3">{t("Nothing found")}</div>}

                        {!dishSupplementsLoading && supplementGroups.length === 0 && (
                            <div className="text-muted fs-6 text-center mb-3">
                                {debouncedSearch ? t("Nothing found") : t("There are no additive groups yet")}
                            </div>
                        )}

                        {supplementGroups.map((group) => (
                            <div className="form-check form-switch p-2  mb-2" key={group.id}>
                                <FormCheck
                                    type="checkbox"
                                    id={`supplementGroup-${group.id}-field`}
                                    className="ps-7"
                                    checked={selectedIds.includes(group.id)}
                                    onChange={() => handleToggleGroup(group.id)}
                                    label={
                                        <RenderGroupLabel
                                            name={group.name}
                                            nameOrigin={group.nameOrigin}
                                            options={group.options}
                                            currencySign={currencySign}
                                            isTranslationMode={isTranslationMode}
                                            tooltipText={translationTooltip}
                                        />
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer className="justify-content-between border-top-0">
                {canCreateNewSupplement && (
                    <Button variant="outline-primary" className="custom-btn" onClick={handleCreateNewClick}>
                        {t("Create new")}
                    </Button>
                )}
                <Button
                    variant="primary"
                    className="custom-btn fw-bold"
                    onClick={handleAddClick}
                    disabled={!hasSelection}
                >
                    {t("Add")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddSupplementsToDishModal;
